// SalarySuggestions.js - Componente de sugestões salariais automáticas
import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { 
  Euro, 
  Calculator,
  CheckCircle,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  RefreshCw,
  Gift,
  Percent,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "" || isNaN(value)) return "";
  if (Number(value) === 0) return "";
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyDecimal = (value) => {
  if (value === null || value === undefined || value === "" || isNaN(value)) return "";
  if (Number(value) === 0) return "";
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

const SalarySuggestions = ({ getAuthHeaders, onSelectSuggestion }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  // Tipo de contrato
  const [contractType, setContractType] = useState("contract"); // contract | green_receipts | b2b
  
  // Tipo de salário: bruto ou líquido
  const [salaryType, setSalaryType] = useState("gross"); // gross | net
  
  // Pagamento a 12 ou 14 meses
  const [paymentMonths, setPaymentMonths] = useState(14); // 12 | 14
  
  // Rate do cliente
  const [clientRate, setClientRate] = useState("");
  const [rateType, setRateType] = useState("hourly"); // hourly, daily, monthly
  
  // Salário
  const [salary, setSalary] = useState("");
  
  // Prazo de pagamento
  const [paymentTerms, setPaymentTerms] = useState(30);
  
  // Configurações
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [workingDaysMonth, setWorkingDaysMonth] = useState(22);
  const [billableMonths, setBillableMonths] = useState(11);
  
  // Benefícios - Valor em € ou %
  const [benefitsMode, setBenefitsMode] = useState("percent"); // percent | euro
  const [benefitsPercent, setBenefitsPercent] = useState(15);
  const [benefitsEuro, setBenefitsEuro] = useState("");
  
  // Campos opcionais manuais (vazios por defeito)
  const [customDuodecimos, setCustomDuodecimos] = useState("");
  const [customSemDuodecimos, setCustomSemDuodecimos] = useState("");
  const [irsType, setIrsType] = useState("single");
  const [dependents, setDependents] = useState(0);
  
  // Convert rate to monthly
  const monthlyClientRate = useMemo(() => {
    const rate = parseFloat(clientRate) || 0;
    if (rateType === "hourly") {
      return rate * hoursPerDay * workingDaysMonth;
    } else if (rateType === "daily") {
      return rate * workingDaysMonth;
    }
    return rate;
  }, [clientRate, rateType, hoursPerDay, workingDaysMonth]);

  // Calculate effective benefits amount
  const effectiveBenefitsAmount = useMemo(() => {
    const salaryValue = parseFloat(salary) || 0;
    if (benefitsMode === "euro") {
      return parseFloat(benefitsEuro) || 0;
    }
    return salaryValue * (benefitsPercent / 100);
  }, [salary, benefitsMode, benefitsEuro, benefitsPercent]);

  // Calculate a specific scenario
  const calculateScenario = async (grossSalaryValue, benefitsValue) => {
    try {
      // Determine benefits based on value
      const mealAllowanceDaily = benefitsValue > 50 ? 10.49 : (benefitsValue > 0 ? 6.00 : 0);
      const healthInsurance = benefitsValue > 100 ? 50 : 0;
      const coverflexValue = Math.max(0, benefitsValue - (mealAllowanceDaily * workingDaysMonth) - healthInsurance);
      
      const payload = {
        business_type: "outsourcing",
        regime_type: contractType,
        contract_type: "outsourcing",
        gross_value: grossSalaryValue,
        value_type: "monthly",
        hours_per_month: hoursPerDay * workingDaysMonth,
        hours_per_day: hoursPerDay,
        irs_type: irsType,
        dependents: dependents,
        irs_jovem: false,
        include_meal_allowance: mealAllowanceDaily > 0,
        meal_allowance_daily: mealAllowanceDaily,
        meal_allowance_card: true,
        include_health_insurance: healthInsurance > 0,
        health_insurance: healthInsurance,
        km_allowance: 0,
        insurance_capitalization: 0,
        ticket_infancia: 0,
        coverflex: coverflexValue,
        client_rate: monthlyClientRate || (grossSalaryValue * 1.5),
        client_rate_type: "monthly",
        billable_months: billableMonths,
        payment_terms: 30,
        working_days_month: workingDaysMonth,
        selected_benefits: [],
      };

      const response = await axios.post(`${API}/simulator/calculate`, payload, {
        headers: getAuthHeaders(),
      });

      const result = response.data.results?.[0];
      if (!result) return null;

      // Calculate duodecimos variations
      const grossMonthly = result.gross_monthly || grossSalaryValue;
      const subsVacations = grossMonthly;
      const subsChristmas = grossMonthly;
      
      // Com duodécimos: use valor customizado se preenchido
      const calculatedNetWithDuodecimos = result.net_monthly + ((subsVacations + subsChristmas) / 12 * 0.75);
      const netWithDuodecimos = customDuodecimos ? parseFloat(customDuodecimos) : calculatedNetWithDuodecimos;
      
      // Sem duodécimos: use valor customizado se preenchido
      const calculatedNetWithoutDuodecimos = result.net_monthly;
      const netWithoutDuodecimos = customSemDuodecimos ? parseFloat(customSemDuodecimos) : calculatedNetWithoutDuodecimos;

      return {
        grossSalary: grossSalaryValue,
        dailyRate: grossSalaryValue / workingDaysMonth,
        benefitsAmount: benefitsValue,
        benefitsPercent: grossSalaryValue > 0 ? (benefitsValue / grossSalaryValue * 100) : 0,
        actualMargin: result.margin_percentage,
        marginAmount: result.margin_amount,
        netMonthly: result.net_monthly,
        netAnnual: result.net_annual,
        netWithDuodecimos,
        netWithoutDuodecimos,
        totalCompanyCost: result.total_company_cost,
        clientRevenue: result.client_revenue,
        scoreLabel: result.score_label,
        scoreColor: result.score_color,
        breakdown: result.breakdown,
        mealAllowance: mealAllowanceDaily * workingDaysMonth,
        healthInsurance: healthInsurance,
        coverflex: coverflexValue,
        totalBenefits: (mealAllowanceDaily * workingDaysMonth) + healthInsurance + coverflexValue,
        irsType,
        contractType,
      };
    } catch (error) {
      console.error("Error calculating scenario:", error);
      return null;
    }
  };

  // Generate suggestions
  const generateSuggestions = async () => {
    const salaryValue = parseFloat(salary);
    const rate = parseFloat(clientRate);
    
    if (!salaryValue && !rate) {
      toast.error("Introduza o Rate do Cliente ou o Salário");
      return;
    }

    setLoading(true);
    try {
      const scenarios = [];
      
      // Se o tipo de salário for líquido, precisamos calcular o bruto correspondente
      let baseGrossSalary = salaryValue;
      if (salaryType === "net" && salaryValue) {
        // Estimativa: bruto ≈ líquido / 0.7 (considerando ~30% de descontos)
        baseGrossSalary = salaryValue / 0.70;
      }
      
      // Ajustar para 12 ou 14 meses se necessário
      if (paymentMonths === 12 && salaryValue) {
        // Se o user inseriu salário a 12 meses, converter para mensal (a 14)
        baseGrossSalary = baseGrossSalary * 14 / 12;
      }
      
      if (rate && !salaryValue) {
        // Generate from rate - calculate possible salaries
        const baseMonthly = monthlyClientRate;
        const margins = [5, 7, 10, 12, 15, 18, 20, 25]; // Ordem crescente
        
        for (const margin of margins) {
          const costFactor = 1.35;
          const maxGrossSalary = (baseMonthly * (1 - margin / 100)) / costFactor;
          
          if (maxGrossSalary > 500) {
            const roundedSalary = Math.round(maxGrossSalary / 50) * 50;
            const benefits = roundedSalary * (benefitsPercent / 100);
            const result = await calculateScenario(roundedSalary, benefits);
            if (result) {
              // Adicionar info sobre tipo de salário e meses
              result.salaryType = salaryType;
              result.paymentMonths = paymentMonths;
              scenarios.push(result);
            }
          }
        }
      } else {
        // Generate from salary - different benefit levels (ordem crescente)
        const baseSalary = baseGrossSalary;
        const benefitsLevels = [0, 5, 10, 15, 20, 25, 30];
        
        for (const percent of benefitsLevels) {
          const benefits = baseSalary * (percent / 100);
          const result = await calculateScenario(baseSalary, benefits);
          if (result) {
            // Adicionar info sobre tipo de salário e meses
            result.salaryType = salaryType;
            result.paymentMonths = paymentMonths;
            result.inputSalary = salaryValue;
            result.inputSalaryType = salaryType;
            scenarios.push(result);
          }
        }
      }
      
      // Ordenar por % de benefícios crescente
      scenarios.sort((a, b) => a.benefitsPercent - b.benefitsPercent);
      
      setSuggestions(scenarios);
      
      if (scenarios.length === 0) {
        toast.warning("Não foi possível gerar sugestões");
      } else {
        toast.success(`${scenarios.length} sugestões geradas`);
      }
    } catch (error) {
      console.error("Error generating suggestions:", error);
      toast.error("Erro ao gerar sugestões");
    } finally {
      setLoading(false);
    }
  };

  // Recalculate when slider changes
  useEffect(() => {
    if (suggestions.length > 0 && salary) {
      // Regenerate with new benefits percentage
      generateSuggestions();
    }
  }, [benefitsPercent]);

  const getScoreColorClass = (color) => {
    const colors = {
      "green": "bg-brand-100 text-brand-800 border-brand-300",
      "lime": "bg-lime-100 text-lime-800 border-lime-300",
      "yellow": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "orange": "bg-orange-100 text-orange-800 border-orange-300",
      "red": "bg-red-100 text-red-800 border-red-300"
    };
    return colors[color] || "bg-gray-100 text-gray-800";
  };

  const getScoreIcon = (color) => {
    if (color === "green" || color === "lime") return <CheckCircle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  // Contract type label
  const getContractLabel = () => {
    switch (contractType) {
      case "green_receipts": return "Recibos Verdes";
      case "b2b": return "B2B / Unipessoal";
      default: return "Contrato de Trabalho";
    }
  };

  return (
    <Card className="bg-gradient-to-br from-violet-50 to-indigo-50 border-2 border-violet-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-violet-800 flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Sugestões Salariais
        </CardTitle>
        <p className="text-sm text-violet-600">
          Compare salários com diferentes níveis de benefícios
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Tipo de Contrato */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-violet-700">Tipo de Contrato</Label>
          <Select value={contractType} onValueChange={setContractType}>
            <SelectTrigger className="bg-white border-violet-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="contract">Contrato de Trabalho</SelectItem>
              <SelectItem value="green_receipts">Recibos Verdes</SelectItem>
              <SelectItem value="b2b">B2B / Unipessoal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Valores de Entrada - Reorganizado */}
        <div className="p-3 bg-white rounded-lg border border-violet-200">
          <div className="text-xs font-medium text-violet-700 mb-3">Valores de Entrada</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Prazo de Pagamento - PRIMEIRO */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Prazo Pag.</Label>
              <Select value={paymentTerms.toString()} onValueChange={(v) => setPaymentTerms(parseInt(v))}>
                <SelectTrigger className="h-9 text-sm bg-white border-violet-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Rate do Cliente */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Rate Cliente</Label>
              <div className="relative">
                <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-violet-400" />
                <Input
                  type="number"
                  placeholder="27"
                  className="pl-7 h-9 text-sm bg-white border-violet-200"
                  value={clientRate}
                  onChange={(e) => setClientRate(e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Tipo Rate</Label>
              <Select value={rateType} onValueChange={setRateType}>
                <SelectTrigger className="h-9 text-sm bg-white border-violet-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hora</SelectItem>
                  <SelectItem value="daily">Dia</SelectItem>
                  <SelectItem value="monthly">Mês</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meses Fat. - ANTES DO SALÁRIO */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Meses Fat.</Label>
              <Input
                type="number"
                className="h-9 text-sm bg-white border-violet-200"
                value={billableMonths}
                onChange={(e) => setBillableMonths(parseFloat(e.target.value) || 11)}
              />
            </div>

            {/* Salário - ÚLTIMO */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">
                {salaryType === "gross" ? "Salário Bruto" : "Salário Líquido"}
              </Label>
              <div className="relative">
                <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-violet-400" />
                <Input
                  type="number"
                  placeholder="2500"
                  className="pl-7 h-9 text-sm bg-white border-violet-200"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          {/* Segunda linha: Tipo de salário e Pagamento 12/14 */}
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-violet-100">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Tipo de Salário</Label>
              <Select value={salaryType} onValueChange={setSalaryType}>
                <SelectTrigger className="h-9 text-sm bg-white border-violet-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gross">Bruto</SelectItem>
                  <SelectItem value="net">Líquido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Pagamento</Label>
              <Select value={paymentMonths.toString()} onValueChange={(v) => setPaymentMonths(parseInt(v))}>
                <SelectTrigger className="h-9 text-sm bg-white border-violet-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12 meses</SelectItem>
                  <SelectItem value="14">14 meses</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview do Rate Mensal */}
          {clientRate && (
            <div className="mt-3 pt-3 border-t border-violet-100 flex justify-between text-sm">
              <span className="text-violet-600">Rate Mensal:</span>
              <span className="font-mono font-bold text-violet-800">{formatCurrency(monthlyClientRate)}</span>
            </div>
          )}
        </div>

        {/* Benefícios - € ou % */}
        <div className="p-3 bg-white rounded-lg border border-violet-200">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-xs font-medium text-violet-700 flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Benefícios / Ajudas
            </Label>
            <div className="flex gap-1 p-0.5 bg-violet-100 rounded">
              <button
                type="button"
                onClick={() => setBenefitsMode("percent")}
                className={`px-2 py-1 text-xs rounded transition-all ${
                  benefitsMode === "percent" ? "bg-violet-600 text-white" : "text-violet-600"
                }`}
              >
                <Percent className="w-3 h-3 inline" /> %
              </button>
              <button
                type="button"
                onClick={() => setBenefitsMode("euro")}
                className={`px-2 py-1 text-xs rounded transition-all ${
                  benefitsMode === "euro" ? "bg-violet-600 text-white" : "text-violet-600"
                }`}
              >
                <Euro className="w-3 h-3 inline" /> €
              </button>
            </div>
          </div>
          
          {benefitsMode === "percent" ? (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Percentagem do salário</span>
                <span className="font-mono font-bold text-violet-700">{benefitsPercent}%</span>
              </div>
              <Slider
                value={[benefitsPercent]}
                onValueChange={([v]) => setBenefitsPercent(v)}
                max={30}
                min={0}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>0%</span>
                <span>15% (recomendado)</span>
                <span>30%</span>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-violet-400" />
              <Input
                type="number"
                placeholder="300"
                className="pl-7 h-9 text-sm bg-white border-violet-200"
                value={benefitsEuro}
                onChange={(e) => setBenefitsEuro(e.target.value)}
              />
              <span className="text-xs text-muted-foreground mt-1 block">Valor mensal em euros</span>
            </div>
          )}
        </div>

        {/* Campos Opcionais - MANUAIS (vazios por defeito) */}
        <div className="p-3 bg-white rounded-lg border border-violet-200">
          <div className="text-xs font-medium text-violet-700 mb-3">
            Campos Opcionais (Manual)
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-600">Líquido c/ Duodéc.</Label>
              <div className="relative">
                <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input
                  type="number"
                  placeholder=""
                  className="pl-6 h-8 text-xs bg-slate-50 border-slate-200"
                  value={customDuodecimos}
                  onChange={(e) => setCustomDuodecimos(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-600">Líquido s/ Duodéc.</Label>
              <div className="relative">
                <Euro className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input
                  type="number"
                  placeholder=""
                  className="pl-6 h-8 text-xs bg-slate-50 border-slate-200"
                  value={customSemDuodecimos}
                  onChange={(e) => setCustomSemDuodecimos(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-600">Tipo de IRS</Label>
              <Select value={irsType} onValueChange={setIrsType}>
                <SelectTrigger className="h-8 text-xs bg-slate-50 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Não Casado</SelectItem>
                  <SelectItem value="married_one">Casado 1 Tit.</SelectItem>
                  <SelectItem value="married_two">Casado 2 Tit.</SelectItem>
                  <SelectItem value="divorced">Divorciado(a)</SelectItem>
                  <SelectItem value="widowed">Viúvo(a)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-slate-600">Dependentes</Label>
              <Select value={dependents.toString()} onValueChange={(v) => setDependents(parseInt(v))}>
                <SelectTrigger className="h-8 text-xs bg-slate-50 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <Button 
          onClick={generateSuggestions}
          disabled={loading || (!clientRate && !salary)}
          className="w-full bg-[var(--brand-primary)] hover:bg-[#143838] text-white font-semibold"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Calculator className="w-4 h-4 mr-2" />
          )}
          Gerar Sugestões
        </Button>

        {/* Info about contract type differences */}
        {contractType !== "contract" && (
          <div className="p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-700">
            <strong>{getContractLabel()}</strong>: Os cálculos consideram taxas e regimes específicos para este tipo de contrato.
          </div>
        )}

        {/* Results Table */}
        {suggestions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-violet-100">
                  <th className="p-2 text-left font-semibold text-violet-800 border-b border-violet-200">
                    Bruto
                  </th>
                  <th className="p-2 text-center font-semibold text-violet-800 border-b border-violet-200">
                    Margem
                  </th>
                  <th className="p-2 text-center font-semibold text-violet-800 border-b border-violet-200">
                    s/ Duodéc.
                  </th>
                  <th className="p-2 text-center font-semibold text-violet-800 border-b border-violet-200">
                    c/ Duodéc.
                  </th>
                  <th className="p-2 text-center font-semibold text-violet-800 border-b border-violet-200">
                    Ajudas
                  </th>
                  <th className="p-2 text-center font-semibold text-violet-800 border-b border-violet-200">
                    Score
                  </th>
                  <th className="p-2 text-center font-semibold text-violet-800 border-b border-violet-200">
                    
                  </th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s, idx) => (
                  <tr 
                    key={idx} 
                    className={`border-b border-violet-100 hover:bg-violet-50 transition-colors ${
                      s.actualMargin >= 15 ? 'bg-brand-50/50' : 
                      s.actualMargin >= 10 ? 'bg-lime-50/50' : 
                      s.actualMargin >= 7 ? 'bg-yellow-50/50' : 
                      'bg-orange-50/50'
                    }`}
                  >
                    <td className="p-2">
                      <div className="flex flex-col">
                        <span className="font-bold">{formatCurrency(s.grossSalary)}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {formatCurrencyDecimal(s.dailyRate)}/dia
                        </span>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-bold">{s.actualMargin?.toFixed(1)}%</span>
                        <span className="text-[10px] text-muted-foreground">{formatCurrency(s.marginAmount)}</span>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <span>{formatCurrencyDecimal(s.netWithoutDuodecimos)}</span>
                    </td>
                    <td className="p-2 text-center">
                      <span className="font-bold text-violet-700">{formatCurrencyDecimal(s.netWithDuodecimos)}</span>
                    </td>
                    <td className="p-2 text-center">
                      <div className="flex flex-col items-center">
                        <span className="text-xs">{s.benefitsPercent?.toFixed(0)}%</span>
                        <span className="text-[10px] text-muted-foreground">{formatCurrency(s.totalBenefits)}</span>
                      </div>
                    </td>
                    <td className="p-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getScoreColorClass(s.scoreColor)}`}>
                        {getScoreIcon(s.scoreColor)}
                        {s.scoreLabel}
                      </span>
                    </td>
                    <td className="p-2 text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onSelectSuggestion?.({
                          ...s,
                          contractType,
                        })}
                        className="h-7 px-2 text-violet-600 hover:text-violet-800 hover:bg-violet-100"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-brand-100 border border-brand-300" />
              Excelente (≥15%)
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-lime-100 border border-lime-300" />
              Muito Bom (≥10%)
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300" />
              Bom (≥7%)
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-orange-100 border border-orange-300" />
              Aceitável (&lt;7%)
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalarySuggestions;
